// Planche du pass de la saison 2 « L’ATELIER DU PÔLE » (outil de développement, aucun serveur, aucune donnée réelle) :
//   /?sheet=pass   la vraie page du pass dans plusieurs états, empilés à 1440 px pour les captures de Léni :
//                  (a) saison en cours, niveau 37, gratuit ; (b) premium, niveau 105, tout réclamé ;
//                  (c) moins de 24 h avant la fin ; (d) vitrine des 24 cosmétiques de la saison.
// Le tableau est construit comme sur le serveur : pièces, gemmes et jetons de la saison 1 niveau par niveau,
// objets remplacés par S2_COSMETIC_REWARDS (shared/seasons.ts).
import { COSMETIC_DEF, RARITY_COLOR } from '../../../shared/cosmetics';
import { S2_COSMETIC_REWARDS, SEASON_DEFS } from '../../../shared/seasons';
import type { MeResponse } from '../game/api';
import { css } from '../game/colors';
import { t } from '../i18n';
import { seasonName } from '../i18n/catalog';
import { Icon } from './icons';
import { cosName, rarityName } from './PaintPage';
import { DEFAULT_PASS_CONFIG, PassPage, type PassTable } from './PassPage';
import { RewardArt, s1PassLevels, slotLabel, type PassLevel, type Reward } from './rewards';

const W = 1440;
const S2 = SEASON_DEFS.find((s) => s.id === 'S2')!;
const HOUR = 3_600_000;

const item = (id: string): Reward => ({ kind: 'item', item: id, label: COSMETIC_DEF[id]?.name ?? id });

const LEVELS: PassLevel[] = s1PassLevels().map((l) => {
  const c = S2_COSMETIC_REWARDS[l.level];
  return c ? { level: l.level, free: item(c.free), premium: item(c.premium) } : l;
});
const TABLE: PassTable = { seasonId: S2.id, levels: LEVELS, config: DEFAULT_PASS_CONFIG, info: S2, next: null };

const DAILY = [
  { id: 'd_shapes', label: 'Détruire 60 formes', target: 60 },
  { id: 'd_active', label: 'Jouer 15 minutes actives', target: 15 },
  { id: 'd_kills', label: 'Éliminer 3 tanks', target: 3 },
];
const WEEKLY = [
  { id: 'w_active', label: 'Jouer 120 minutes actives', target: 120 },
  { id: 'w_monsters', label: 'Vaincre 60 monstres', target: 60 },
  { id: 'w_boss', label: 'Participer à la victoire contre 2 boss', target: 2 },
  { id: 'w_shapes', label: 'Détruire 500 formes', target: 500 },
];

/** Profil fictif : niveau, XP dans le niveau, premium, récompenses réclamées jusqu'au niveau atteint. */
function demoMe(o: { id: string; level: number; xpIn: number; premium: boolean; progress: number[]; gems: number }): MeResponse {
  const claimed: string[] = [];
  for (let lv = 1; lv <= o.level; lv++) {
    claimed.push(`${lv}:free`);
    if (o.premium) claimed.push(`${lv}:premium`);
  }
  const rows = (list: typeof DAILY, from: number) =>
    list.map((m, i) => {
      const progress = Math.min(m.target, Math.round(m.target * (o.progress[(from + i) % o.progress.length] ?? 0)));
      return { id: m.id, label: m.label, target: m.target, progress, done: progress >= m.target };
    });
  return {
    account: { publicId: `demo-${o.id}`, name: 'Léni', email: 'demo@tankshift.local', verified: true },
    guest: false,
    balances: { coins: 18_450, gems: o.gems },
    pass: {
      season: { id: S2.id, name: S2.name, startsAt: S2.startsAt, endsAt: S2.endsAt },
      ended: false,
      xp: (o.level - 1) * DEFAULT_PASS_CONFIG.xpPerLevel + o.xpIn,
      level: o.level,
      premium: o.premium,
      claimed,
    },
    missions: { daily: rows(DAILY, 0), weekly: rows(WEEKLY, 3), dailyReward: { coins: 100, passXp: 1000 }, weeklyReward: { coins: 500, passXp: 5000 } },
  };
}

const STATES: { key: string; title: string; sub: string; me: MeResponse; now: number }[] = [
  {
    key: 'a',
    title: 'A · Saison 2 en cours',
    sub: 'Niveau 37, pass gratuit : récompenses gratuites réclamées, premium verrouillé. 12 décembre, 14 h 20 UTC.',
    me: demoMe({ id: 'a', level: 37, xpIn: 1240, premium: false, progress: [1, 0.6, 0.33, 0.8, 1, 0.25, 0.1], gems: 1_250 }),
    now: Date.UTC(2026, 11, 12, 14, 20),
  },
  {
    key: 'b',
    title: 'B · Premium, niveau 105',
    sub: 'Pass premium, niveau maximal, toutes les récompenses réclamées. 27 décembre, 9 h UTC.',
    me: demoMe({ id: 'b', level: 105, xpIn: 0, premium: true, progress: [1, 1, 1, 1, 1, 0.9, 1], gems: 340 }),
    now: Date.UTC(2026, 11, 27, 9, 0),
  },
  {
    key: 'c',
    title: 'C · Moins de 24 h avant la fin',
    sub: 'Niveau 88, premium : compte à rebours en heures et minutes. 31 décembre, 18 h 47 UTC.',
    me: demoMe({ id: 'c', level: 88, xpIn: 620, premium: true, progress: [0.5, 1, 0, 0.7, 0.4, 1, 0.2], gems: 90 }),
    now: S2.endsAt - 5 * HOUR - 13 * 60_000,
  },
];

const noop = () => {};

/** Vitrine des 24 cosmétiques de la saison 2 : niveau, piste, nom, rareté, aperçu réel. */
function Showcase() {
  const rows = Object.entries(S2_COSMETIC_REWARDS).flatMap(([lvl, r]) => [
    { level: Number(lvl), track: 'free' as const, id: r.free },
    { level: Number(lvl), track: 'premium' as const, id: r.premium },
  ]);
  return (
    <div className="page pass-sheet-showcase">
      <div className="pass-sheet-grid">
        {rows.map(({ level, track, id }) => {
          const d = COSMETIC_DEF[id];
          return (
            <article key={id} className={`plate pass-sheet-card ${track}`} style={{ borderTopColor: d ? css(RARITY_COLOR[d.rarity]) : undefined }}>
              <span className="eyebrow">
                {t('level', { n: level })} · {track === 'premium' ? (
                  <span className="pass-sheet-premium">
                    <Icon name="star" size={12} />
                    {t('passPremium')}
                  </span>
                ) : (
                  t('passFree')
                )}
              </span>
              <span className="pass-sheet-art">
                <RewardArt r={item(id)} size={96} />
              </span>
              <strong className="pass-sheet-name">{cosName(id)}</strong>
              <span className="pass-sheet-meta">
                {d && slotLabel(d.slot)}
                {d && (
                  <span className="rarity-tag" style={{ color: css(RARITY_COLOR[d.rarity]) }}>
                    {rarityName(d.rarity)}
                  </span>
                )}
              </span>
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function PassSheet() {
  return (
    <div className="pass-sheet" style={{ width: '100%', maxWidth: W }} data-testid="pass-sheet">
      {STATES.map((s) => (
        <section key={s.key} className="pass-sheet-state" aria-label={s.title}>
          <header className="pass-sheet-head">
            <h2 className="display">{s.title}</h2>
            <p className="wip">{s.sub}</p>
          </header>
          <PassPage me={s.me} onChanged={noop} go={noop} table={TABLE} now={s.now} demo />
        </section>
      ))}
      <section className="pass-sheet-state" aria-label="D">
        <header className="pass-sheet-head">
          <h2 className="display">D · {seasonName(S2.id, S2.name)} : les 24 récompenses</h2>
          <p className="wip">Vrais cosmétiques de la collection {t('col_noel')}, exclusifs au pass, dessinés avec le rendu du jeu (effets : pictogramme de leur emplacement).</p>
        </header>
        <Showcase />
      </section>
    </div>
  );
}
